// The one request helper of the client (R1706, ui-map-interface.md §16.12).
//
// A browser keeps one HTTP/3 connection per origin. A home router drops
// the UDP mapping of that connection after about 30 s of silence, and the
// browser does not know: the next request goes on the dead connection and
// waits with no answer. No browser API can force a new connection. Only a
// later attempt can reach one, after the browser drops the dead one.
//
// So a SAFE request (GET, HEAD, or a request the caller marks idempotent)
// makes a short first attempt, then more attempts with a pause between
// them, until one answers or the budget is spent. A request that is not
// safe makes ONE attempt, because a second attempt could send a purchase,
// a report or a sign-in twice.
//
// An attempt is retried on a network failure (a `TypeError`) and on its
// own timeout. An HTTP status is an answer, so a 4xx or a 5xx is never
// retried. The caller's own signal ends the request at once.
//
// The helper touches no DOM. The clock, the fetch and the online test are
// injectable, so the whole rule is one pure test.

import {
  API_TIMEOUT_MS, REQUEST_BACKOFF_MAX_MS, REQUEST_BACKOFF_MS, REQUEST_BUDGET_MS,
  REQUEST_FIRST_TIMEOUT_MS, REQUEST_MIN_ATTEMPT_MS,
} from '../gen/presentation';

/** The figures of the retry, from `[presentation.net]`. */
export interface RequestPolicy {
  /** The wait of the first attempt of a safe request. */
  firstTimeoutMs: number;
  /** The longest wait of one attempt; also the wait of a request that is
   *  not safe. */
  maxTimeoutMs: number;
  /** The first pause between two attempts. */
  backoffMs: number;
  /** The longest pause between two attempts. */
  backoffMaxMs: number;
  /** The time all attempts of one safe request share. */
  budgetMs: number;
  /** No attempt starts with less of the budget left than this. */
  minAttemptMs: number;
}

export const DEFAULT_POLICY: RequestPolicy = {
  firstTimeoutMs: REQUEST_FIRST_TIMEOUT_MS,
  maxTimeoutMs: API_TIMEOUT_MS,
  backoffMs: REQUEST_BACKOFF_MS,
  backoffMaxMs: REQUEST_BACKOFF_MAX_MS,
  budgetMs: REQUEST_BUDGET_MS,
  minAttemptMs: REQUEST_MIN_ATTEMPT_MS,
};

/** What the helper reads outside itself. Each one has a real default. */
export interface RequestDeps {
  fetch?: typeof fetch;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (id: unknown) => void;
  now?: () => number;
  /** False when the device says it has no network: no retry then. */
  online?: () => boolean;
}

export interface RequestOptions extends RequestDeps {
  /** True when a second send does no harm. The default is true for GET
   *  and HEAD, and false for every other method. */
  idempotent?: boolean;
  /** The caller's signal. Its abort ends the request at once. */
  signal?: AbortSignal;
  policy?: Partial<RequestPolicy>;
}

/** The error of an attempt, or of a request, that waited too long. Its
 *  name is the name the platform gives a timed-out signal. */
export class RequestTimeoutError extends Error {
  constructor() {
    super('the request timed out');
    this.name = 'TimeoutError';
  }
}

/** True for a method that a second send cannot harm. */
export function isSafeMethod(method: string | undefined): boolean {
  const m = (method ?? 'GET').toUpperCase();
  return m === 'GET' || m === 'HEAD';
}

/** True for the rejection of an attempt or a request that waited too long
 *  (R1203): `RequestTimeoutError`, or the platform's timed-out signal. */
export function isTimeoutError(err: unknown): boolean {
  return err instanceof Error && err.name === 'TimeoutError';
}

/** True for a failure that a new attempt can cure: a network failure or
 *  a timeout. Every other error is the caller's. */
export function isRetryable(err: unknown): boolean {
  return err instanceof TypeError || isTimeoutError(err);
}

/** The wait of attempt `n` (0 is the first): it doubles from the first
 *  wait up to the longest. */
export function attemptTimeoutMs(n: number, p: RequestPolicy = DEFAULT_POLICY): number {
  return Math.min(p.firstTimeoutMs * 2 ** n, p.maxTimeoutMs);
}

/** The pause after attempt `n`: it doubles from the first pause up to the
 *  longest. */
export function backoffMs(n: number, p: RequestPolicy = DEFAULT_POLICY): number {
  return Math.min(p.backoffMs * 2 ** n, p.backoffMaxMs);
}

/** The error a caller's abort gives, where its signal names no reason. */
function abortError(signal: AbortSignal): unknown {
  if (signal.reason !== undefined) return signal.reason;
  const e = new Error('the request was aborted');
  e.name = 'AbortError';
  return e;
}

interface Clock {
  set: (fn: () => void, ms: number) => unknown;
  clear: (id: unknown) => void;
  now: () => number;
}

function clockOf(o: RequestDeps): Clock {
  return {
    set: o.setTimeout ?? ((fn, ms) => setTimeout(fn, ms)),
    clear: o.clearTimeout ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>)),
    now: o.now ?? (() => Date.now()),
  };
}

function deviceOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/**
 * One attempt: the fetch and the read of its answer, ended by the attempt's
 * timer or by the caller's signal. The race does not trust the fetch to
 * honour its signal: the timer rejects the attempt in any case.
 */
function attempt<T>(url: string, init: RequestInit, read: (res: Response) => Promise<T>,
  limitMs: number, outer: AbortSignal | undefined, f: typeof fetch, clock: Clock,
  bodyAfter: boolean): Promise<T> {
  const ctl = typeof AbortController === 'function' ? new AbortController() : null;
  return new Promise<T>((resolve, reject) => {
    let done = false;
    let timer: unknown = null;
    const finish = (settle: () => void, ok: boolean): void => {
      if (done) return;
      done = true;
      if (timer !== null) clock.clear(timer);
      outer?.removeEventListener('abort', onOuter);
      // The forward of the body read stays only on an answer whose body the
      // caller reads after the attempt; every other end takes it off, so a
      // long-lived caller signal does not collect one listener an attempt.
      if (!(ok && bodyAfter)) outer?.removeEventListener('abort', onBody);
      settle();
    };
    const onOuter = (): void => {
      const err = abortError(outer as AbortSignal);
      ctl?.abort(err);
      finish(() => reject(err), false);
    };
    const onBody = (): void => { ctl?.abort(abortError(outer as AbortSignal)); };
    timer = clock.set(() => {
      const err = new RequestTimeoutError();
      ctl?.abort(err);
      finish(() => reject(err), false);
    }, limitMs);
    outer?.addEventListener('abort', onOuter);
    // The caller's abort reaches the fetch after the attempt too: a reader
    // that took the head alone (`requestHead`) still reads the body on this
    // signal, and its stop must end that read.
    outer?.addEventListener('abort', onBody, { once: true });
    const run = async (): Promise<T> => read(await f(url, { ...init, signal: ctl?.signal }));
    run().then((v) => finish(() => resolve(v), true), (e: unknown) => finish(() => reject(e), false));
  });
}

/** A pause of `ms`, ended early by the caller's abort. */
function pause(ms: number, outer: AbortSignal | undefined, clock: Clock): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const onOuter = (): void => {
      clock.clear(timer);
      reject(abortError(outer as AbortSignal));
    };
    const timer = clock.set(() => {
      outer?.removeEventListener('abort', onOuter);
      resolve();
    }, ms);
    outer?.addEventListener('abort', onOuter);
  });
}

/**
 * Sends one request and reads its answer with `read`, with the retry rule
 * above. `read` runs inside the attempt, so a body that stalls times the
 * attempt out the same as a head that never comes. The answer reaches
 * `read` whatever its status: the caller decides what a status means.
 *
 * It rejects with the caller's abort reason, with `RequestTimeoutError`
 * when the last attempt waited too long, and with the last error
 * otherwise.
 */
export function request<T>(url: string, init: RequestInit,
  read: (res: Response) => Promise<T>, opts: RequestOptions = {}): Promise<T> {
  return send(url, init, read, opts, false);
}

/** `request` with the choice of `requestHead`: `bodyAfter` keeps the
 *  forward of the caller's abort on the answer, for the body read that
 *  follows the attempt. */
async function send<T>(url: string, init: RequestInit,
  read: (res: Response) => Promise<T>, opts: RequestOptions, bodyAfter: boolean): Promise<T> {
  const p: RequestPolicy = { ...DEFAULT_POLICY, ...opts.policy };
  const clock = clockOf(opts);
  const f = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const online = opts.online ?? deviceOnline;
  const safe = opts.idempotent ?? isSafeMethod(init.method);
  const outer = opts.signal;
  const deadline = clock.now() + (safe ? p.budgetMs : p.maxTimeoutMs);
  for (let n = 0; ; n++) {
    if (outer?.aborted) throw abortError(outer);
    const left = deadline - clock.now();
    const limit = safe ? Math.min(attemptTimeoutMs(n, p), left) : left;
    try {
      return await attempt(url, init, read, limit, outer, f, clock, bodyAfter);
    } catch (err) {
      if (outer?.aborted) throw abortError(outer);
      if (!safe || !isRetryable(err) || !online()) throw err;
      const wait = backoffMs(n, p);
      if (deadline - clock.now() - wait < p.minAttemptMs) throw err;
      await pause(wait, outer, clock);
    }
  }
}

/**
 * `request` that answers at the head. The retry covers the wait for the
 * head alone, and the caller reads the body after it, on its own clock: a
 * stream with a progress bar, or a file larger than one attempt's wait.
 * The caller's signal still ends that read.
 */
export function requestHead(url: string, init: RequestInit = {},
  opts: RequestOptions = {}): Promise<Response> {
  return send(url, init, (res) => Promise.resolve(res), opts, true);
}

/** `request` with a read of the whole body as text, for a caller that
 *  reads the status and the body together. */
export function requestText(url: string, init: RequestInit = {},
  opts: RequestOptions = {}): Promise<{ res: Response; text: string }> {
  return request(url, init, async (res) => ({ res, text: await res.text() }), opts);
}
